import { useCallback, useState } from 'react';
import {
	validateName,
	validateStreet,
	validateZipCode,
	validateXaddrExt,
	validateXaddrInt,
	validateAddressLine2,
	validateCity,
	validateState,
} from '@/utils/validateAddress';
import { EditableAddress } from '@/data/types/Address';

type Errors = {
	[key: string]: string;
};

const MAP_VALIDATION: Record<string, (value: string) => boolean> = {
	nickName: validateName,
	addressLine1: validateStreet,
	xaddr_numExt: validateXaddrExt,
	xaddr_numInt: validateXaddrInt,
	zipCode: validateZipCode,
	addressLine2: validateAddressLine2,
	state: validateState,
	city: validateCity,
};

const MAP_MESSAGES: Record<string, string> = {
	firstName: 'Campo obligatorio',
	lastName: 'Campo obligatorio',
	lastNameMat: 'Campo obligatorio',
	phone1: 'El teléfono debe tener 10 dígitos',
	email1: 'Capturar formato correcto',
	nickName: 'Campo obligatorio',
	addressLine1: 'Campo obligatorio',
	xaddr_numExt: 'Campo obligatorio',
	xaddr_numInt: 'Campo obligatorio',
	zipCode: 'Campo obligatorio',
	addressLine2: 'Campo obligatorio',
	state: 'Campo obligatorio',
	city: 'Campo obligatorio',
};

const OMIT_READY_VALIDATE = ['xaddr_numInt'];

export const validateField = (name: string, value: string, initialErrors: Errors = {}) => {
	const newErrors: Errors = initialErrors;

	const validator = MAP_VALIDATION[name];

	if (validator && !validator(value)) {
		newErrors[name] = MAP_MESSAGES[name];
	} else {
		delete newErrors[name];
	}

	return newErrors;
};

export const validateAllFields = (address: EditableAddress) => {
	const newErrors: Errors = Object.entries(address).reduce((acc, [key, value]) => {
		const error = validateField(key, value as string);
		return { ...acc, ...error };
	}, {});
	return newErrors;
};

export const isAddressReady = (address: EditableAddress): boolean => {
	const errors = validateAllFields(address);
	return Object.keys(errors).filter((k) => !OMIT_READY_VALIDATE.includes(k)).length === 0;
};

export const useAddressFormValidation = () => {
	const [errors, setErrors] = useState<Errors>({});

	const onReadyValidateAllFields = (address: EditableAddress) => isAddressReady(address);

	const onValidateField = useCallback((name: string, value: string) => {
		setErrors((prevErrors) => {
			const updated = validateField(name, value, { ...prevErrors });
			const prevHasErr = name in prevErrors;
			const newHasErr = name in updated;
			if (!prevHasErr && !newHasErr) return prevErrors;
			if (prevHasErr && newHasErr && prevErrors[name] === updated[name]) return prevErrors;
			return updated;
		});
	}, []);

	const onCleanErrorField = useCallback((name: string) => {
		setErrors((prev) => {
			if (!(name in prev)) return prev;
			const newErrors = { ...prev };
			delete newErrors[name];
			return newErrors;
		});
	}, []);

	const onSetFieldError = useCallback((name: string, message: string) => {
		setErrors((prev) => {
			if (prev[name] === message) return prev;
			return { ...prev, [name]: message };
		});
	}, []);

	return {
		errors,
		onCleanErrorField,
		onReadyValidateAllFields,
		onValidateField,
		onSetFieldError,
	};
};
